import { useState } from 'react';
import { AlertTriangle, RefreshCw, X } from 'lucide-react';
import type { CameraRecord } from '@yawi/types';
import { useServices } from '@/app/providers';
import { useCameras } from '../hooks';
import { CameraCard } from '../components/CameraCard';
import { CameraPlayer } from '../components/CameraPlayer';

export function CamerasPage() {
  const { cameraRepo } = useServices();
  const { cameras, loading, error, connectionOk, refresh } = useCameras();
  const [selectedCamera, setSelectedCamera] = useState<CameraRecord | null>(null);
  const [snapshot, setSnapshot] = useState<string | null>(null);
  const [capturingId, setCapturingId] = useState<string | null>(null);

  async function handleCapture(indexCode: string) {
    setCapturingId(indexCode);
    try {
      const dataUrl = await cameraRepo.captureSnapshot(indexCode);
      setSnapshot(dataUrl);
    } catch {
      // Camera may be unreachable
    } finally {
      setCapturingId(null);
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Cámaras en Vivo</h1>
          {connectionOk !== null && (
            <p className={`text-sm mt-0.5 ${connectionOk ? 'text-green-600' : 'text-red-600'}`}>
              {connectionOk ? 'HikCentral conectado' : 'Sin conexión con HikCentral'}
            </p>
          )}
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800 border border-slate-200 rounded-lg px-3 py-2 hover:bg-slate-50 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {/* Connection error */}
      {connectionOk === false && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
          <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
          <div>
            <p className="font-medium text-red-700">Sin conexión con HikCentral</p>
            <p className="text-sm text-red-600">
              Verifica que el servidor HikCentral esté activo y accesible desde el backend.
            </p>
          </div>
        </div>
      )}

      {/* General error */}
      {error && connectionOk !== false && (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
          <p className="text-sm text-amber-700">{error}</p>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="rounded-xl border border-slate-200 overflow-hidden animate-pulse">
              <div className="aspect-video bg-slate-200" />
              <div className="p-3">
                <div className="h-4 bg-slate-200 rounded w-3/4 mb-2" />
                <div className="h-3 bg-slate-100 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Camera grid */}
      {!loading && cameras.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {cameras.map((cam) => (
            <CameraCard
              key={cam.indexCode}
              camera={cam}
              onSelect={setSelectedCamera}
              onCapture={handleCapture}
              capturing={capturingId === cam.indexCode}
            />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && cameras.length === 0 && connectionOk && (
        <div className="text-center py-16 text-slate-400">
          <p className="text-lg font-medium">No hay cámaras registradas</p>
          <p className="text-sm mt-1">Registra cámaras en HikCentral para verlas aquí.</p>
        </div>
      )}

      {/* Fullscreen modal */}
      {selectedCamera && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex flex-col"
          onClick={() => setSelectedCamera(null)}
        >
          {/* Modal header */}
          <div className="flex items-center justify-between px-4 py-3 bg-black/50">
            <span className="text-white font-medium">{selectedCamera.name}</span>
            <button
              onClick={() => setSelectedCamera(null)}
              className="p-2 rounded-lg hover:bg-white/10 text-white transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Video player full size */}
          <div
            className="flex-1 flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-full max-w-5xl">
              <CameraPlayer indexCode={selectedCamera.indexCode} />
            </div>
          </div>
        </div>
      )}

      {/* Snapshot viewer */}
      {snapshot && (
        <div className="mt-6 p-4 bg-white border border-slate-200 rounded-xl">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800">Última captura</h3>
            <div className="flex items-center gap-2">
              <a
                href={snapshot}
                download="captura.jpg"
                className="text-sm text-primary-600 hover:text-primary-700 underline"
              >
                Descargar
              </a>
              <button
                onClick={() => setSnapshot(null)}
                className="p-1 rounded hover:bg-slate-100 text-slate-400"
                aria-label="Cerrar captura"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <img
            src={snapshot}
            alt="Captura de cámara"
            className="max-w-full rounded-lg border border-slate-200"
          />
        </div>
      )}
    </div>
  );
}
